import React, { useState, useMemo, memo } from 'react'
import * as StickyFooterLayout from 'views/layouts/StickyFooterLayout/StickyFooterLayout'
import { useFormik } from 'formik'
import { SectionContainer, SectionBody, Input, FormComponents, BasicSelect } from '@agro-club/frontend-shared'
import { Progress } from 'modules/types'
import { useTranslation } from 'react-i18next'
import { SeedClassDTO } from 'modules/domain/seedClass/types'
import { useSeedCropList } from 'modules/domain/seedCrop/hooks'
import { SeedCrop } from 'modules/domain/seedCrop/types'
import * as Yup from 'yup'
import useValidationErrorNotification from 'hooks/useValidationErrorNotification'
import StickyFooterDefaultControls from 'views/components/StickyFormControls/StickyFooterDefaultControls'

export type FormData = {
  name: string
  crop_id: string
}

const SeedClassDetailsForm: React.FC<{
  initialValues?: Partial<FormData>
  progress?: Progress
  removeProgress?: Progress
  onSubmit(formFields: Partial<SeedClassDTO>): void
  onCancel(): void
  onRemove?(): void
  mode: 'create' | 'edit'
}> = ({ progress, initialValues, removeProgress, onSubmit, onCancel, onRemove }) => {
  const { t } = useTranslation(['seedClass', 'common'])
  const [submitCount, setSubmitCount] = useState(0)
  const [cropProgress, crops] = useSeedCropList()

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t('form.requiredField')),
        crop_id: Yup.string().required(t('form.requiredField')),
      }),
    [t],
  )

  const formik = useFormik<Partial<FormData>>({
    initialValues: {
      name: initialValues?.name || '',
      crop_id: initialValues?.crop_id || '',
    },
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })

  const handleSubmit = async () => {
    setSubmitCount(submitCount + 1)
    if (!formik.isValid) {
      return
    }

    onSubmit({
      name: formik.values.name,
      crop_id: formik.values.crop_id,
    })
  }

  const selectedCrop = crops?.find((crop) => crop.id === formik.values.crop_id) || null

  useValidationErrorNotification(submitCount, formik.isValid)

  return (
    <StickyFooterLayout.Wrapper>
      <StickyFooterLayout.Body>
        <SectionContainer>
          <SectionBody>
            <FormComponents.FormSection title={t('form.title')}>
              <Input
                {...formik.getFieldProps('name')}
                label={t('form.name')}
                invalid={formik.touched.name && !!formik.errors.name}
                errorText={formik.errors.name}
                required
              />
              <BasicSelect
                getOptionLabel={(opt: SeedCrop) => opt.name}
                getOptionValue={(opt: SeedCrop) => opt.id}
                onChange={(value: SeedCrop | null) => {
                  formik.setFieldValue('crop_id', value?.id || '')
                }}
                placeholder={t('form.cropSelect')}
                options={crops || []}
                value={selectedCrop}
                isSearchable
                invalid={formik.touched.crop_id && !!formik.errors.crop_id}
                errorText={formik.errors.crop_id}
                progress={cropProgress}
                required
                isDisabled={progress === Progress.WORK}
              />
            </FormComponents.FormSection>
          </SectionBody>
        </SectionContainer>
      </StickyFooterLayout.Body>
      <StickyFooterDefaultControls
        onRemove={onRemove}
        onSave={handleSubmit}
        onCancel={onCancel}
        saveProgress={progress}
        isSaveDisabled={!formik.dirty || progress === Progress.WORK}
        removeProgress={removeProgress}
        popoverText={t('form.removeText', { name: initialValues?.name })}
      />
    </StickyFooterLayout.Wrapper>
  )
}

export default memo(SeedClassDetailsForm)
